import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * On route change, scroll to top — or to the hash anchor if one is present.
 * Skips on POP (back/forward) so the browser can restore scroll position.
 * Defers anchor lookup to the next frame so the target element is mounted.
 */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      // The target may be inside a section that mounts after data loads.
      // Poll for up to ~2s before giving up and falling back to top.
      let cancelled = false;
      const deadline = performance.now() + 2000;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ block: "start" });
          return;
        }
        if (performance.now() < deadline) {
          requestAnimationFrame(tryScroll);
        } else {
          window.scrollTo(0, 0);
        }
      };
      requestAnimationFrame(tryScroll);
      return () => {
        cancelled = true;
      };
    }

    // No hash: scroll to top, but let the browser restore position on back/forward.
    if (navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname, hash, key, navType]);

  return null;
}
